import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button, Col, Container, FormGroup, Label, Modal, ModalBody, Row, Input } from 'reactstrap';
import useApi from 'vl-common/src/hooks/useApi';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { LoadingButton } from 'vl-common/src/components/LoadingButton';
import { getMetaAppointmentsQuery } from 'vl-common/src/schemas/getMetaAppointments';
import { filterClinSubtypesByApptType } from 'vl-common/src/lib/filterClinSubtypesByApptType';
import { successModal } from 'vl-common/src/components/Modals';

interface RemoveQuotaProps {
  open: boolean;
  caseId?: number;
  onCancel: () => void;
  onCreated: () => void;
}

export const AddQuotaModal = ({ open, caseId, onCancel, onCreated }: RemoveQuotaProps) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const [userSubtypeCode, setUserSubtypeCode] = useState('');
  const [apptTypeCode, setApptTypeCode] = useState('');
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
  };
  const hideAlertMessage = () => {
    setShowAlertMessage({ visibility: false, msg: '' });
  };

  const { data: appointmentsMeta } = useQuery({
    ...getMetaAppointmentsQuery({ case_id: parseInt(caseId) }),
    enabled: !!parseInt(caseId)
  });

  const clinSubtypes = appointmentsMeta?.clin_subtypes ?? [];
  const quotaApptTypes = appointmentsMeta?.appt_types?.filter((appt_type) => appt_type?.allow_quota) ?? [];

  const { mutate: addAuth, isPending: isUpdatingAuth } = useMutation({
    mutationKey: ['updateAddQuota', caseId],
    mutationFn: async () => {
      const response = await Api.cases.addAuth(caseId, apptTypeCode, userSubtypeCode);
      return response;
    },

    onError: (error: any) => {
      const errorMessage: string = error?.response?.data?.message;
      if (errorMessage) {
        displayAlertMessage(errorMessage);
      } else {
        displayAlertMessage('Bad Request, Try again.');
      }
    },
    onSuccess: () => {
      successModal({ title: 'Added quota successfully' });
      onCreated();
    }
  });

  return (
    <Modal size="lg" isOpen={open} centered className="vl-add-modal" data-testid="remove-quota-modal">
      <ModalBody className="p-4">
        <Container>
          <Row>
            <Col xs="12">
              <span className="vl-modal-title" data-testid="remove-quota-header">
                {t('Add Virtual Quota')}
              </span>
            </Col>
            <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
            <Col>
              <FormGroup>
                <br />
                <Label for="apptTypeSelect">{t('Appointment type')}</Label>
                <Input
                  id="apptTypeSelect"
                  name="apptTypeSelect"
                  type="select"
                  onChange={(e) => {
                    const nextApptTypeCode = e.target.value;
                    setApptTypeCode(nextApptTypeCode);

                    const isStillAvailable = filterClinSubtypesByApptType(clinSubtypes, nextApptTypeCode).some(
                      ({ user_subtype_code }) => user_subtype_code === userSubtypeCode
                    );
                    if (!isStillAvailable) setUserSubtypeCode('');
                  }}
                >
                  <option value="">{t('Select appointment type')}</option>
                  {quotaApptTypes.map((appt_type) => (
                    <option key={appt_type?.appt_type_code} value={appt_type?.appt_type_code}>
                      {appt_type?.appt_type_desc}
                    </option>
                  ))}
                </Input>
                <br />
                <Label for="clinTypeSelect">{t('Clinician type')}</Label>
                <Input
                  id="clinTypeSelect"
                  name="clinTypeSelect"
                  type="select"
                  value={userSubtypeCode}
                  onChange={(e) => setUserSubtypeCode(e.target.value)}
                >
                  <option value="">{t('Select clinician type')}</option>
                  {filterClinSubtypesByApptType(clinSubtypes, apptTypeCode).map((clin_type) => (
                    <option key={clin_type?.user_subtype_code} value={clin_type?.user_subtype_code}>
                      {clin_type?.user_subtype_desc}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs="12" className="text-right" style={styles.buttonContainer}>
              <Button
                className="vl-button-gray vl-font-regular"
                type="button"
                disabled={isUpdatingAuth}
                onClick={() => {
                  hideAlertMessage();
                  onCancel();
                }}
                data-testid="cancel-add-quota-modal-button"
              >
                {t('Cancel')}
              </Button>
              <LoadingButton
                className="vl-button vl-font-regular"
                disabled={isUpdatingAuth || !caseId || !apptTypeCode || !userSubtypeCode}
                onClick={() => {
                  hideAlertMessage();
                  addAuth();
                }}
                loading={isUpdatingAuth}
                data-testid="confirm-add-quota-button"
              >
                {t('Add Quota')}
              </LoadingButton>
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};
const styles = {
  info: { marginBottom: '1rem' },
  label: { marginBottom: '1rem', width: '50%' },
  buttonContainer: { display: 'flex', justifyContent: 'flex-end' }
};

export default AddQuotaModal;
